import type { StreamHit } from './stream'

/**
 * The stream's columns beyond the fixed row (CLI-758 follow-up): what the analytics build put on
 * an article that Headline has no field of its own for. The stream blob carries every column the
 * build wrote except the per-query bookkeeping (analytics-dashboard-backend `trim_stream_article`),
 * so a client's custom classification, the narrative cluster it was put in, EMV, a brand-mention
 * flag or a column added next month all reach the row — some as named fields, the rest in
 * `StreamHit.fields` under the name the build gave them.
 *
 * Nothing here knows which client carries which column. `inferStreamColumns` looks at the rows
 * that were served and says what each column is — a label to group by, several labels, a number
 * to add up, a yes/no — so the Library can offer it as a column and Derek as a dimension or a
 * measure, for any client, the day it appears.
 */

/** A custom column's value on one row, as the reader kept it — see `toCustomFields`. */
export type StreamFieldValue = string | number | boolean | string[]

/** Columns every client may carry, given names of their own on the row. */
export const STREAM_NAMED_FIELDS = [
  'country',
  'language',
  'emv',
  'mention',
  'narrative',
  'injected',
] as const
export type StreamNamedField = (typeof STREAM_NAMED_FIELDS)[number]
/** What reading a column needs of a row — a `StreamHit`, or any narrower row that carries these. */
export type StreamFieldRow = Pick<StreamHit, StreamNamedField | 'fields'>

const NAMED_LABELS: Record<StreamNamedField, string> = {
  country: 'Country',
  language: 'Language',
  emv: 'EMV (est.)',
  mention: 'Brand mention',
  narrative: 'Narrative',
  injected: 'Hand-curated',
}

/** What a column holds, judged from its values — what it can be used for follows from it. */
export const STREAM_COLUMN_KINDS = ['category', 'multi', 'number', 'boolean', 'text'] as const
export type StreamColumnKind = (typeof STREAM_COLUMN_KINDS)[number]

export interface StreamColumn {
  /** A named field (`country`, `emv`, …) or the build's own name for the column. */
  key: string
  label: string
  /**
   * - `category`: one label per row, few enough to group by (a classification, a tier).
   * - `multi`: several labels per row (companies mentioned) — a row counts once in each.
   * - `number`: a figure to add up or average (EMV, a theme score).
   * - `boolean`: yes / no (a brand mention, hand-curated).
   * - `text`: a label on nearly every row with nearly every value different (an id, a sentence)
   *   — shown, never grouped by.
   */
  kind: StreamColumnKind
  /** Rows that carry a value. */
  present: number
  /** Distinct values seen (labels for category/multi; capped at `DISTINCT_CAP`). */
  distinct: number
  /** The most frequent labels, for a picker or a prompt; empty for numbers and yes/no. */
  top: string[]
}

const DISTINCT_CAP = 2000
const TOP_LABELS = 8

/** A row's value for a column: a named field, else the build's own column. Absent is undefined. */
export function streamFieldValue(hit: StreamFieldRow, key: string): StreamFieldValue | undefined {
  if ((STREAM_NAMED_FIELDS as readonly string[]).includes(key)) {
    const value = hit[key as StreamNamedField]
    return value === null || value === undefined ? undefined : value
  }
  return hit.fields?.[key]
}

/** A value as labels: a category's one, a multi's several, a yes/no's "Yes"; none for a number. */
export function streamFieldLabels(value: StreamFieldValue | undefined): string[] {
  if (value === undefined) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'boolean') return value ? ['Yes'] : ['No']
  if (typeof value === 'number') return [String(value)]
  return [value]
}

/** A column's name for people: `classification_matching_query` → "Classification matching
 *  query", `ThemeScores.AI` → "Theme scores · AI", `OutletTier` → "Outlet tier". */
export function streamColumnLabel(key: string): string {
  if ((STREAM_NAMED_FIELDS as readonly string[]).includes(key)) {
    return NAMED_LABELS[key as StreamNamedField]
  }
  return key
    .split('.')
    .map((part) =>
      part
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        // An acronym stays one (AI, EMV, L1); every other word reads in sentence case.
        .map((word) => (word.length > 1 && word === word.toUpperCase() ? word : word.toLowerCase()))
        .join(' '),
    )
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' · ')
}

/** What one column's values have been, counted across the rows. */
interface Tally {
  present: number
  numbers: number
  booleans: number
  lists: number
  counts: Map<string, number>
}

function tallyValue(entry: Tally, value: StreamFieldValue) {
  entry.present += 1
  if (typeof value === 'number') {
    entry.numbers += 1
    return
  }
  if (typeof value === 'boolean') entry.booleans += 1
  else if (Array.isArray(value)) entry.lists += 1
  for (const label of streamFieldLabels(value)) {
    if (entry.counts.size >= DISTINCT_CAP && !entry.counts.has(label)) continue
    entry.counts.set(label, (entry.counts.get(label) ?? 0) + 1)
  }
}

function columnKind(entry: Tally): StreamColumnKind {
  if (entry.numbers === entry.present) return 'number'
  if (entry.booleans === entry.present) return 'boolean'
  if (entry.lists > 0) return 'multi'
  return entry.present >= 50 && entry.counts.size > entry.present * 0.6 ? 'text' : 'category'
}

/**
 * Every column beyond the fixed row that the served rows carry, most widely carried first. A
 * column no row has a value for is not one. Judged from the values, never from the name: a
 * column is a number when every value is, a yes/no when every value is, several labels when any
 * value is a list, and a label otherwise — `text` when it is on most rows and almost never
 * repeats, which no chart can group by.
 */
export function inferStreamColumns(hits: readonly StreamFieldRow[]): StreamColumn[] {
  const tallies = new Map<string, Tally>()
  const tally = (key: string, value: StreamFieldValue | undefined) => {
    if (value === undefined) return
    let entry = tallies.get(key)
    if (!entry) {
      entry = { present: 0, numbers: 0, booleans: 0, lists: 0, counts: new Map() }
      tallies.set(key, entry)
    }
    tallyValue(entry, value)
  }
  for (const hit of hits) {
    for (const key of STREAM_NAMED_FIELDS) tally(key, streamFieldValue(hit, key))
    for (const [key, value] of Object.entries(hit.fields ?? {})) tally(key, value)
  }
  const columns: StreamColumn[] = []
  for (const [key, entry] of tallies) {
    const kind = columnKind(entry)
    const top =
      kind === 'category' || kind === 'multi'
        ? [...entry.counts.entries()]
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .slice(0, TOP_LABELS)
            .map(([label]) => label)
        : []
    columns.push({
      key,
      label: streamColumnLabel(key),
      kind,
      present: entry.present,
      distinct: kind === 'number' ? 0 : entry.counts.size,
      top,
    })
  }
  return columns.sort((a, b) => b.present - a.present || a.label.localeCompare(b.label))
}
